import { forwardRef, useEffect, useId, useState } from 'react';
import { IconButton } from '@mweb/zappos-ui/IconButton';
import type { IconButtonSize, IconButtonVariant } from '@mweb/zappos-ui/IconButton';
import { InputMessage } from '@mweb/zappos-ui/InputMessage';

import {
  containerBaseClasses,
  containerSizeClasses,
  containerVariantClasses,
  countBaseClasses,
  countSizeClasses,
  countVariantClasses,
  iconVariantClasses,
  wrapperClasses
} from 'components/common/QuantitySelector/QuantitySelector.classes';
import type QuantitySelectorProps from 'components/common/QuantitySelector/QuantitySelector.types';
import {
  getCount,
  getIsActionsDisabled,
  getIsDecrementDisabled,
  getIsIncrementDisabled
} from 'components/common/QuantitySelector/QuantitySelector.utility';
import { cn } from 'helpers/classnames';
import { getTestId } from 'utils/testing';
import MinusIcon from 'components/common/IconsV2/MinusIcon';
import MinusSmallIcon from 'components/common/IconsV2/MinusSmallIcon';
import PlusSmallIcon from 'components/common/IconsV2/PlusSmallIcon';
import PlusIcon from 'components/common/IconsV2/PlusIcon';

export const QuantitySelector = forwardRef<HTMLDivElement, QuantitySelectorProps>((props: QuantitySelectorProps, ref) => {
  const {
    size = 'large',
    variant = 'base',
    baseCount,
    maxCount,
    errorMsg,
    onQuantityChange,
    className,
    containerClassName,
    testId,
    ...forwardProps
  } = props;
  const [count, setCount] = useState(baseCount);
  const iconClasses = iconVariantClasses[variant];
  const iconButtonProps: { size: IconButtonSize; variant: IconButtonVariant } = {
    size: 'small',
    variant: 'filledModerate'
  };
  const ERROR_ID = useId();
  const QUANTITY_INPUT_ID = useId();
  const QUANTITY_VALUE_ID = useId();

  useEffect(() => {
    if (baseCount > 0) {
      setCount(baseCount);
    }
  }, [baseCount]);

  const changeCount = (isIncrement: boolean) => {
    const newCount = getCount({ count, isIncrement, maxCount });
    setCount(newCount);
    onQuantityChange?.({ isIncrement: isIncrement, value: newCount });
  };

  return (
    <div className={cn(wrapperClasses, className)} data-test-id={getTestId(testId)}>
      <div
        className={cn(containerBaseClasses, containerSizeClasses[size], containerVariantClasses[variant], containerClassName)}
        {...forwardProps}
        ref={ref}
      >
        <IconButton
          className="h-fit w-fit rounded-sm active:bg-gray-200 hover:enabled:bg-gray-200 zappos:active:bg-yellow-200 zappos:enabled:hover:bg-yellow-100" // 6pm support
          aria-describedby={`${QUANTITY_INPUT_ID} ${QUANTITY_VALUE_ID}`}
          onClick={() => changeCount(false)}
          data-test-id={getTestId(testId, 'decrement')}
          disabled={getIsDecrementDisabled({ count, variant })}
          label="decrement"
          linkComponent={null}
          linkProps={{}}
          {...iconButtonProps}
        >
          {size === 'large' && (
            <>
              <MinusIcon className={iconClasses} size={24} />
            </>
          )}
          {size === 'small' && (
            <>
              <MinusSmallIcon className={iconClasses} size={16} />
            </>
          )}
        </IconButton>
        <input
          id={QUANTITY_INPUT_ID}
          className={cn(countBaseClasses, countSizeClasses[size], countVariantClasses[variant])}
          value={count}
          type="number"
          readOnly
          aria-readonly
          data-test-id={getTestId(testId, 'input')}
          disabled={getIsActionsDisabled({ variant })}
          {...(variant === 'error' ? { 'aria-errormessage': ERROR_ID, 'aria-invalid': true } : {})}
        />
        <label htmlFor={QUANTITY_INPUT_ID} className="sr-only">
          Quantity:
        </label>
        <span id={QUANTITY_VALUE_ID} className="sr-only">
          {count}
        </span>
        <IconButton
          className="h-fit w-fit rounded-sm active:bg-gray-200 hover:enabled:bg-gray-200 zappos:active:bg-yellow-200 zappos:enabled:hover:bg-yellow-100" // 6pm support
          aria-describedby={`${QUANTITY_INPUT_ID} ${QUANTITY_VALUE_ID}`}
          onClick={() => changeCount(true)}
          label="increment"
          data-test-id={getTestId(testId, 'increment')}
          disabled={getIsIncrementDisabled({ count, maxCount, variant })}
          linkComponent={null}
          linkProps={{}}
          {...iconButtonProps}
        >
          {size === 'large' && (
            <>
              <PlusIcon className={iconClasses} size={24} />
            </>
          )}
          {size === 'small' && (
            <>
              <PlusSmallIcon className={iconClasses} size={16} />
            </>
          )}
        </IconButton>
      </div>
      {variant === 'error' && errorMsg && (
        <InputMessage id={ERROR_ID} variant="error">
          {errorMsg}
        </InputMessage>
      )}
    </div>
  );
});
